import { MutableRefObject, RefObject, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useGetHistoryMoves } from 'modules/Room/Hooks/useGetHistoryMoves';

import { OptimizedImage } from 'shared/components/ui/optimized-image/optimized-image';

import emblem from 'assets/png/emblem.png?format=webp;avif&as=picture';

import { StreamDesktopHistoryMove } from './stream-desktop-history-move';

interface IStreamDesktopHistoryMovesProps {
  currentRound?: number;
  forPlayer: 'playerA' | 'playerB';
  selfRef: RefObject<HTMLDivElement>;
  syncRef: RefObject<HTMLDivElement>;
  syncLockRef: MutableRefObject<boolean>;
  isGameFinished?: boolean;
}

export const StreamDesktopHistoryMoves = ({
  currentRound,
  forPlayer,
  isGameFinished,
  selfRef,
  syncRef,
  syncLockRef,
}: IStreamDesktopHistoryMovesProps) => {
  const gameHistory = useGetHistoryMoves(currentRound, true);
  const { activeRoomId } = useParams();
  const [isScrollable, setIsScrollable] = useState(false);

  const reversedHistory = [...gameHistory].reverse();
  const visibleRounds = reversedHistory.filter((_, i) => {
    return (currentRound && i < currentRound - 1) || isGameFinished;
  });

  useEffect(() => {
    const el = selfRef.current;
    if (!el) return;

    const handleScroll = () => {
      if (syncLockRef.current) return;

      syncLockRef.current = true;

      if (syncRef.current) {
        syncRef.current.scrollTop = el.scrollTop;
      }

      requestAnimationFrame(() => {
        syncLockRef.current = false;
      });
    };

    el.addEventListener('scroll', handleScroll);

    return () => {
      el.removeEventListener('scroll', handleScroll);
    };
  }, [selfRef, syncRef, syncLockRef]);

  useEffect(() => {
    const checkScrollable = () => {
      const container = selfRef.current;
      if (!container) return;

      const children = Array.from(container.children);
      let totalHeight = 0;

      children.forEach((child) => {
        const styles = window.getComputedStyle(child as HTMLElement);
        const marginTop = parseInt(styles.marginTop || '0', 10);
        const marginBottom = parseInt(styles.marginBottom || '0', 10);
        totalHeight +=
          (child as HTMLElement).getBoundingClientRect().height + marginTop + marginBottom;
      });

      const containerHeight = container.clientHeight;
      setIsScrollable(totalHeight > containerHeight);
    };

    requestAnimationFrame(checkScrollable);
    window.addEventListener('resize', checkScrollable);
    return () => {
      window.removeEventListener('resize', checkScrollable);
    };
  }, [visibleRounds.length, selfRef]);

  if (!activeRoomId) return null;
  return (
    <>
      <div className="h-[238px] w-[2px] bg-indigo" />
      <div className="relative h-full max-h-[1080px] w-full overflow-hidden">
        <div
          ref={selfRef}
          className={`no-scrollbar absolute bottom-0 top-0 flex h-full w-full flex-col-reverse items-center gap-3 overflow-y-auto whitespace-nowrap ${
            !isScrollable ? 'justify-end' : ''
          }`}
        >
          {currentRound === 1 && (
            <div
              className="h-[76px] w-[60px] rounded-[12px] p-[1px]"
              style={{
                background:
                  'linear-gradient(180deg, rgba(50, 46, 102, 1) 0%, rgba(55, 52, 112, 1) 100%)',
              }}
            >
              <div
                className="relative flex max-h-[76px] max-w-[60px] shrink-0 items-center justify-center overflow-hidden rounded-[12px] sm:h-[108px] sm:w-[80px]"
                style={{
                  background:
                    'linear-gradient(180deg, rgba(21, 23, 40, 1) 0%, rgba(29, 33, 62, 1) 100%)',
                }}
              >
                <OptimizedImage
                  alt="emblem"
                  className="h-[40px] w-[40px]"
                  style={{ objectFit: 'cover' }}
                  imageData={emblem}
                />
              </div>
            </div>
          )}
          {visibleRounds.map((round, index) => (
            <StreamDesktopHistoryMove
              key={round.encrMoveA + index}
              round={round}
              forPlayer={forPlayer}
              show
              index={index}
              totalVisible={visibleRounds.length}
            />
          ))}
        </div>
      </div>
      <div className="h-[238px] w-[2px] bg-indigo" />
    </>
  );
};
